import React, {useCallback, useRef, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Colors, CtaButton, Text} from '@optimaxdev/design-system';
import {Klarna, KlarnaWhite, Icon} from '@optimaxdev/icons';
import {useSelector} from 'react-redux';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {useGetErrorAndSendAnalytics} from 'desktop/pages/checkout/hooks/primer/useGetErrorAndSendAnalytics/useGetErrorAndSendAnalytics';
import {usePrimerKlarnaNativeButton} from 'desktop/pages/checkout/hooks/primer/usePrimerKlarnaNativeButton';
import {getIsPlacingOrder} from 'desktop/pages/checkout/store/selectors/completeOrder/completeOrder';

import {getPaymentMethodSaveLoading} from 'desktop/pages/checkout/store/selectors/paymentStep/paymentMethodSave/paymentMethodSave';

import {primerKlarnaOptions} from '../../../paymentKlarnaPrimer/constants';
import {PaymentFooterWrapper} from '../../common/paymentFooter/paymentFooterWrapper';

import {PaymentWrapper} from '../../common/paymentWrapper/paymentWrapper';

import s from './paymentKlarnaPrimerOneStep.scss';

/**
 * Klarna with primer UI component
 */
export const PaymentKlarnaPrimerOneStep = () => {
    const isLoading = useSelector(getPaymentMethodSaveLoading);
    const isPlacingOrder = useSelector(getIsPlacingOrder);

    const onSubmitRef = useRef<(() => void) | null>(null);
    const [isAvailable, setIsAvailable] = useState(false);

    const {isValidating} = usePrimerKlarnaNativeButton({
        onPaymentMethodCategoriesChange: useCallback((categories, manager) => {
            if (categories.length > 0) {
                manager.renderCategory({
                    containerId: primerKlarnaOptions.id,
                    // required by Primer SDK types, but we don't need it
                    onHeightChange: () => {},
                    paymentMethodCategoryId: categories[0].id,
                });
                setIsAvailable(true);
                onSubmitRef.current = () => {
                    manager.start({
                        paymentMethodCategoryId: categories[0].id,
                    });
                };
            }
        }, []),
    });

    const {error} = useGetErrorAndSendAnalytics(PAYMENTS.METHOD_PRIMER_KLARNA);

    const sendAnalytics = () => {
        sendDesktopAnalyticsEvent(
            CHECKOUT_PAYMENT_STEP,
            {
                message: 'CTA - Klarna Primer',
                method: 'Payment',
            },
            true,
        );
    };

    return (
        <>
            <PaymentWrapper errorMessage={error} showSkeleton={!isAvailable}>
                <Icon height="24px" icon={Klarna} />
                <Text color={Colors.limedSpruce} lineHeight="22px" padding="0 0 0 10px" size="14px">
                    Klarna
                </Text>
            </PaymentWrapper>

            <PaymentFooterWrapper isLoading={!isAvailable || Boolean(error)} label="Klarna">
                {!error && <div className={s.klarna} id={primerKlarnaOptions.id} />}

                {!error && isAvailable && (
                    <CtaButton
                        onClick={() => {
                            onSubmitRef.current?.();
                            sendAnalytics();
                        }}
                        ariaLabel="Place Order"
                        className={s.button}
                        isDisabled={Boolean(error) || isLoading}
                        isLoading={isLoading || isPlacingOrder || isValidating}
                        size="large"
                        type="button"
                        isFull
                    >
                        <span>
                            Place Order
                            <Icon className={s.icon} height={15} icon={KlarnaWhite} width={64} />
                        </span>
                    </CtaButton>
                )}
            </PaymentFooterWrapper>
        </>
    );
};
